import { CardContent, Separator } from '@ais-ui/ui-core';
import { MethydTargetInfo } from '@ais-ui/websockets';
import React from 'react';
import { useSettings } from '../../Settings/SettingsProvider';

interface MethydInfoTooltipProps {
  methydInfo: MethydTargetInfo;
}

interface MethydInfoTooltipItemProps {
  label: string;
  value: string;
}

const MethydInfoTooltipItem = ({
  label,
  value,
}: MethydInfoTooltipItemProps) => {
  return (
    <div className="grid grid-cols-[1fr_auto] gap-4 max-w-[12rem]">
      <p className="text-xs font-medium flex-none text-left max-w-[6rem]">
        {label}
      </p>
      <p className="text-xs text-muted-foreground flex-grow text-right">
        {value ? value : 'Unknown'}
      </p>
    </div>
  );
};

export const MethydInfoTooltip = ({ methydInfo }: MethydInfoTooltipProps) => {
  const { settings } = useSettings();
  const ribbonSettings = settings['ribbonPlugin.settings'];

  // Check if all settings are falsy or corresponding data is missing
  const hasAirRelatedData =
    ribbonSettings?.show_methyd_air_related &&
    (methydInfo.airPressureTendency ||
      methydInfo.airPressure ||
      methydInfo.airTemperature ||
      methydInfo.averageWindSpeed ||
      methydInfo.dewPoint ||
      methydInfo.relativeHumidity ||
      methydInfo.windDirection ||
      methydInfo.windGustDirection ||
      methydInfo.windGust);

  const hasWaterRelatedData =
    ribbonSettings?.show_methyd_water_related &&
    (methydInfo.currentDirection2 ||
      methydInfo.currentDirection3 ||
      methydInfo.currentMeasuringLevel2 ||
      methydInfo.currentMeasuringLevel3 ||
      methydInfo.currentSpeed2 ||
      methydInfo.currentSpeed3 ||
      methydInfo.salinity ||
      methydInfo.significantWaveHeight ||
      methydInfo.surfaceCurrentDirection ||
      methydInfo.surfaceCurrentSpeedIncludingTide ||
      methydInfo.swellDirection ||
      methydInfo.swellHeight ||
      methydInfo.swellPeriod ||
      methydInfo.waterLevelIncludingTide ||
      methydInfo.waterLevelTrend ||
      methydInfo.waterTemperature ||
      methydInfo.waveDirection ||
      methydInfo.wavePeriod);

  const hasMetadataInfo =
    (ribbonSettings?.show_methyd_last_origin && methydInfo.lastOrigin) ||
    (ribbonSettings?.show_methyd_location && methydInfo.location) ||
    (ribbonSettings?.show_methyd_message && methydInfo.message) ||
    (ribbonSettings?.show_methyd_latitude && methydInfo.metHydLatitude) ||
    (ribbonSettings?.show_methyd_longitude && methydInfo.metHydLongitude) ||
    (ribbonSettings?.show_latitude_degrees && methydInfo.latitude) ||
    (ribbonSettings?.show_longitude_degrees && methydInfo.longitude) ||
    (ribbonSettings?.show_methyd_mmsi && methydInfo.metHydMmsi);

  // Return null if no items will be shown
  if (!hasAirRelatedData && !hasWaterRelatedData && !hasMetadataInfo) {
    return null;
  }

  return (
    <CardContent className="pt-0 pb-2 -mt-1 space-y-2 px-3">
      <Separator className="-ml-1"></Separator>
      <div className="grid grid-cols-[auto_auto] gap-x-4 gap-y-1">
        {ribbonSettings?.show_methyd_air_related &&
          methydInfo.airPressureTendency && (
            <MethydInfoTooltipItem
              key="airPressureTendency"
              label="Air Pressure Tendency"
              value={methydInfo.airPressureTendency}
            ></MethydInfoTooltipItem>
          )}
        {ribbonSettings?.show_methyd_air_related && methydInfo.airPressure && (
          <MethydInfoTooltipItem
            key="airPressure"
            label="Air Pressure"
            value={methydInfo.airPressure}
          ></MethydInfoTooltipItem>
        )}
        {ribbonSettings?.show_methyd_air_related &&
          methydInfo.airTemperature && (
            <MethydInfoTooltipItem
              key="airTemperature"
              label="Air Temperature"
              value={methydInfo.airTemperature}
            ></MethydInfoTooltipItem>
          )}
        {ribbonSettings?.show_methyd_air_related &&
          methydInfo.averageWindSpeed && (
            <MethydInfoTooltipItem
              key="averageWindSpeed"
              label="Average Wind Speed"
              value={methydInfo.averageWindSpeed}
            ></MethydInfoTooltipItem>
          )}
        {ribbonSettings?.show_methyd_water_related &&
          methydInfo.currentDirection2 && (
            <MethydInfoTooltipItem
              key="currentDirection2"
              label="Current Direction #2"
              value={methydInfo.currentDirection2}
            ></MethydInfoTooltipItem>
          )}
        {ribbonSettings?.show_methyd_water_related &&
          methydInfo.currentDirection3 && (
            <MethydInfoTooltipItem
              key="currentDirection3"
              label="Current Direction #3"
              value={methydInfo.currentDirection3}
            ></MethydInfoTooltipItem>
          )}
        {ribbonSettings?.show_methyd_water_related &&
          methydInfo.currentMeasuringLevel2 && (
            <MethydInfoTooltipItem
              key="currentMeasuringLevel2"
              label="Current Measuring Level #2"
              value={methydInfo.currentMeasuringLevel2}
            ></MethydInfoTooltipItem>
          )}
        {ribbonSettings?.show_methyd_water_related &&
          methydInfo.currentMeasuringLevel3 && (
            <MethydInfoTooltipItem
              key="currentMeasuringLevel3"
              label="Current Measuring Level #3"
              value={methydInfo.currentMeasuringLevel3}
            ></MethydInfoTooltipItem>
          )}
        {ribbonSettings?.show_methyd_water_related &&
          methydInfo.currentSpeed2 && (
            <MethydInfoTooltipItem
              key="currentSpeed2"
              label="Current Speed #2"
              value={methydInfo.currentSpeed2}
            ></MethydInfoTooltipItem>
          )}
        {ribbonSettings?.show_methyd_water_related &&
          methydInfo.currentSpeed3 && (
            <MethydInfoTooltipItem
              key="currentSpeed3"
              label="Current Speed #3"
              value={methydInfo.currentSpeed3}
            ></MethydInfoTooltipItem>
          )}
        {ribbonSettings?.show_methyd_air_related && methydInfo.dewPoint && (
          <MethydInfoTooltipItem
            key="dewPoint"
            label="Dew Point"
            value={methydInfo.dewPoint}
          ></MethydInfoTooltipItem>
        )}
        {ribbonSettings?.show_methyd_last_origin && methydInfo.lastOrigin && (
          <MethydInfoTooltipItem
            key="lastOrigin"
            label="Last Origin"
            value={methydInfo.lastOrigin}
          ></MethydInfoTooltipItem>
        )}
        {ribbonSettings?.show_methyd_location && methydInfo.location && (
          <MethydInfoTooltipItem
            key="location"
            label="Location"
            value={methydInfo.location}
          ></MethydInfoTooltipItem>
        )}
        {ribbonSettings?.show_methyd_message && methydInfo.message && (
          <MethydInfoTooltipItem
            key="message"
            label="Message"
            value={methydInfo.message}
          ></MethydInfoTooltipItem>
        )}
        {ribbonSettings?.show_methyd_latitude && methydInfo.metHydLatitude && (
          <MethydInfoTooltipItem
            key="metHydLatitude"
            label="MetHyd Latitude"
            value={methydInfo.metHydLatitude}
          ></MethydInfoTooltipItem>
        )}
        {ribbonSettings?.show_methyd_longitude &&
          methydInfo.metHydLongitude && (
            <MethydInfoTooltipItem
              key="metHydLongitude"
              label="MetHyd Longitude"
              value={methydInfo.metHydLongitude}
            ></MethydInfoTooltipItem>
          )}
        {ribbonSettings?.show_latitude_degrees && methydInfo.latitude && (
          <MethydInfoTooltipItem
            key="latitude"
            label="Latitude"
            value={methydInfo.latitude.toString()}
          ></MethydInfoTooltipItem>
        )}
        {ribbonSettings?.show_longitude_degrees && methydInfo.longitude && (
          <MethydInfoTooltipItem
            key="longitude"
            label="Longitude"
            value={methydInfo.longitude.toString()}
          ></MethydInfoTooltipItem>
        )}
        {ribbonSettings?.show_methyd_mmsi && methydInfo.metHydMmsi && (
          <MethydInfoTooltipItem
            key="metHydMmsi"
            label="MMSI"
            value={methydInfo.metHydMmsi}
          ></MethydInfoTooltipItem>
        )}
        {ribbonSettings?.show_methyd_air_related &&
          methydInfo.relativeHumidity && (
            <MethydInfoTooltipItem
              key="relativeHumidity"
              label="Relative Humidity"
              value={methydInfo.relativeHumidity}
            ></MethydInfoTooltipItem>
          )}
        {ribbonSettings?.show_methyd_water_related && methydInfo.salinity && (
          <MethydInfoTooltipItem
            key="salinity"
            label="Salinity"
            value={methydInfo.salinity}
          ></MethydInfoTooltipItem>
        )}
        {ribbonSettings?.show_methyd_water_related &&
          methydInfo.significantWaveHeight && (
            <MethydInfoTooltipItem
              key="significantWaveHeight"
              label="Significant Wave Height"
              value={methydInfo.significantWaveHeight}
            ></MethydInfoTooltipItem>
          )}
        {ribbonSettings?.show_methyd_water_related &&
          methydInfo.surfaceCurrentDirection && (
            <MethydInfoTooltipItem
              key="surfaceCurrentDirection"
              label="Surface Current Direction"
              value={methydInfo.surfaceCurrentDirection}
            ></MethydInfoTooltipItem>
          )}
        {ribbonSettings?.show_methyd_water_related &&
          methydInfo.surfaceCurrentSpeedIncludingTide && (
            <MethydInfoTooltipItem
              key="surfaceCurrentSpeedIncludingTide"
              label="Surface Current Speed Including Tide"
              value={methydInfo.surfaceCurrentSpeedIncludingTide}
            ></MethydInfoTooltipItem>
          )}
        {ribbonSettings?.show_methyd_water_related &&
          methydInfo.swellDirection && (
            <MethydInfoTooltipItem
              key="swellDirection"
              label="Swell Direction"
              value={methydInfo.swellDirection}
            ></MethydInfoTooltipItem>
          )}
        {ribbonSettings?.show_methyd_water_related &&
          methydInfo.swellHeight && (
            <MethydInfoTooltipItem
              key="swellHeight"
              label="Swell Height"
              value={methydInfo.swellHeight}
            ></MethydInfoTooltipItem>
          )}
        {ribbonSettings?.show_methyd_water_related &&
          methydInfo.swellPeriod && (
            <MethydInfoTooltipItem
              key="swellPeriod"
              label="Swell Period"
              value={methydInfo.swellPeriod}
            ></MethydInfoTooltipItem>
          )}
        {ribbonSettings?.show_methyd_water_related &&
          methydInfo.waterLevelIncludingTide && (
            <MethydInfoTooltipItem
              key="waterLevelIncludingTide"
              label="Water Level Including Tide"
              value={methydInfo.waterLevelIncludingTide}
            ></MethydInfoTooltipItem>
          )}
        {ribbonSettings?.show_methyd_water_related &&
          methydInfo.waterLevelTrend && (
            <MethydInfoTooltipItem
              key="waterLevelTrend"
              label="Water Level Trend"
              value={methydInfo.waterLevelTrend}
            ></MethydInfoTooltipItem>
          )}
        {ribbonSettings?.show_methyd_water_related &&
          methydInfo.waterTemperature && (
            <MethydInfoTooltipItem
              key="waterTemperature"
              label="Water Temperature"
              value={methydInfo.waterTemperature}
            ></MethydInfoTooltipItem>
          )}
        {ribbonSettings?.show_methyd_water_related &&
          methydInfo.waveDirection && (
            <MethydInfoTooltipItem
              key="waveDirection"
              label="Wave Direction"
              value={methydInfo.waveDirection}
            ></MethydInfoTooltipItem>
          )}
        {ribbonSettings?.show_methyd_water_related && methydInfo.wavePeriod && (
          <MethydInfoTooltipItem
            key="wavePeriod"
            label="Wave Period"
            value={methydInfo.wavePeriod}
          ></MethydInfoTooltipItem>
        )}
        {ribbonSettings?.show_methyd_air_related &&
          methydInfo.windDirection && (
            <MethydInfoTooltipItem
              key="windDirection"
              label="Wind Direction"
              value={methydInfo.windDirection}
            ></MethydInfoTooltipItem>
          )}
        {ribbonSettings?.show_methyd_air_related &&
          methydInfo.windGustDirection && (
            <MethydInfoTooltipItem
              key="windGustDirection"
              label="Wind Gust Direction"
              value={methydInfo.windGustDirection}
            ></MethydInfoTooltipItem>
          )}
        {ribbonSettings?.show_methyd_air_related && methydInfo.windGust && (
          <MethydInfoTooltipItem
            key="windGust"
            label="Wind Gust"
            value={methydInfo.windGust}
          ></MethydInfoTooltipItem>
        )}
      </div>
    </CardContent>
  );
};
